import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Filter, ChevronRight, X } from "lucide-react";
import { useTranslation } from 'react-i18next';

export default function LocationFilters({
  selectedDistrict,
  setSelectedDistrict,
  selectedTaluk,
  setSelectedTaluk,
  selectedHobli,
  setSelectedHobli,
  selectedVillage,
  setSelectedVillage,
  districts,
  taluks,
  hoblis,
  villages,
  ENABLED_DISTRICT,
  ENABLED_TALUK,
  setViewMode,  // Add setViewMode prop to reset view on clear
  user,  // User object to check department
}) {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language === 'kn' ? 'kn' : 'en';
  
  // Check if user is from Survey department (no Circle concept for Survey)
  const isSurveyUser = user?.department === 'survey';
  
  // Helper to get display name from location object (handles both old string format and new API format)
  const getDisplayName = (location) => {
    if (!location) return '';
    if (typeof location === 'string') return location;
    // New API format with name_en and name_kn
    return currentLang === 'kn' ? (location.name_kn || location.name_en) : location.name_en;
  };

  // Helper to get key from location (handles both old string format and new API format)
  const getKey = (location) => {
    if (!location) return '';
    if (typeof location === 'string') return location;
    return location.key;
  };

  // Helper to check if location is active
  const isActive = (location) => {
    if (!location) return false;
    if (typeof location === 'string') return location === ENABLED_DISTRICT || location === ENABLED_TALUK;
    return location.is_active !== false;
  };

  return (
    <Card className="p-3 sm:p-4 bg-white border border-stone-200">
      <div className="flex items-center gap-2 mb-3 sm:mb-4">
        <Filter className="w-4 h-4 text-slate-500" />
        <span className="text-xs sm:text-sm font-medium text-slate-700">{t('locationReports.filterByLocation')}</span>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
        {/* District */}
        <div>
          <label className="text-[10px] sm:text-xs text-slate-500 mb-1 block">{t('newApplication.district')}</label>
          <Select 
            value={selectedDistrict || "all"} 
            onValueChange={(val) => {
              const selected = districts.find(d => getKey(d) === val);
              // Only allow selection of active districts
              if (selected && isActive(selected)) {
                setSelectedDistrict(val === "all" ? ENABLED_DISTRICT : val);
              }
            }}
          >
            <SelectTrigger data-testid="filter-district" className="text-xs sm:text-sm">
              <SelectValue placeholder={t('locationReports.allDistricts')} />
            </SelectTrigger>
            <SelectContent>
              {districts.map(d => {
                const key = getKey(d);
                const active = isActive(d);
                return (
                  <SelectItem 
                    key={key} 
                    value={key}
                    disabled={!active}
                    className={!active ? "text-slate-400 cursor-not-allowed" : ""}
                  >
                    {getDisplayName(d)}{!active && ` (${t('common.comingSoon') || 'ಶೀಘ್ರದಲ್ಲಿ'})`}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        {/* Taluk */}
        <div>
          <label className="text-[10px] sm:text-xs text-slate-500 mb-1 block">{t('newApplication.taluk')}</label>
          <Select 
            value={selectedTaluk || "all"} 
            onValueChange={(val) => {
              const selected = taluks.find(tk => getKey(tk) === val);
              // Only allow selection of active taluks
              if (selected && isActive(selected)) {
                setSelectedTaluk(val === "all" ? ENABLED_TALUK : val);
              }
            }} 
            disabled={!selectedDistrict}
          >
            <SelectTrigger data-testid="filter-taluk" className="text-xs sm:text-sm">
              <SelectValue placeholder={selectedDistrict ? t('locationReports.allTaluks') : t('newApplication.selectDistrictFirst')} />
            </SelectTrigger>
            <SelectContent>
              {taluks.map(tk => {
                const key = getKey(tk);
                const active = isActive(tk);
                return (
                  <SelectItem 
                    key={key} 
                    value={key}
                    disabled={!active}
                    className={!active ? "text-slate-400 cursor-not-allowed" : ""}
                  >
                    {getDisplayName(tk)}{!active && ` (${t('common.comingSoon') || 'ಶೀಘ್ರದಲ್ಲಿ'})`}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        {/* Hobli */}
        <div>
          <label className="text-[10px] sm:text-xs text-slate-500 mb-1 block">{t('newApplication.hobli')}</label>
          <Select 
            value={selectedHobli || "all"} 
            onValueChange={(val) => {
              const newHobli = val === "all" ? "" : val;
              setSelectedHobli(newHobli);
              // For Revenue users: Auto-switch to Detailed View when a specific hobli is selected
              // For Survey users: Stay in Summary View to show Village-wise
              if (newHobli && setViewMode && !isSurveyUser) {
                setViewMode("detailed");
              }
            }} 
            disabled={!selectedTaluk}
          >
            <SelectTrigger data-testid="filter-hobli" className="text-xs sm:text-sm">
              <SelectValue placeholder={selectedTaluk ? t('locationReports.allHoblis') : t('newApplication.selectTalukFirst')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('locationReports.allHoblis')}</SelectItem>
              {hoblis.map(h => {
                const key = getKey(h);
                return (
                  <SelectItem key={key} value={key}>{getDisplayName(h)}</SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>

        {/* Village */}
        <div>
          <label className="text-[10px] sm:text-xs text-slate-500 mb-1 block">{t('newApplication.village')}</label>
          <Select 
            value={selectedVillage || "all"} 
            onValueChange={(val) => {
              const newVillage = val === "all" ? "" : val;
              setSelectedVillage(newVillage);
              // Auto-switch to Detailed View when a specific village is selected
              if (newVillage && setViewMode) {
                setViewMode("detailed");
              }
            }} 
            disabled={!selectedHobli}
          >
            <SelectTrigger data-testid="filter-village" className="text-xs sm:text-sm">
              <SelectValue placeholder={selectedHobli ? `${t('locationReports.allVillages')} (${villages.length})` : t('newApplication.selectHobliFirst')}>
                {selectedVillage && selectedVillage !== "all" ? selectedVillage : (selectedHobli ? `${t('locationReports.allVillages')} (${villages.length})` : t('newApplication.selectHobliFirst'))}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('locationReports.allVillages')} ({villages.length})</SelectItem>
              {villages.map(v => {
                const key = getKey(v);
                const displayName = getDisplayName(v);
                return (
                  <SelectItem key={key} value={key}>{displayName}</SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Current Filter Path (Breadcrumb) with Clear Filters */}
      {selectedDistrict && (
        <div className="mt-3 sm:mt-4 flex flex-wrap items-center gap-1 text-xs sm:text-sm">
          <span className="text-slate-500">{t('locationReports.viewing')}:</span>
          
          <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] sm:text-xs">
            {getDisplayName(districts.find(d => getKey(d) === selectedDistrict)) || selectedDistrict}
          </Badge>
          {selectedTaluk && (
            <>
              <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-slate-400" />
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] sm:text-xs">
                {getDisplayName(taluks.find(tk => getKey(tk) === selectedTaluk)) || selectedTaluk} {t('newApplication.talukSuffix')}
              </Badge>
            </>
          )}
          {selectedHobli && (
            <>
              <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-slate-400" />
              <Badge 
                variant="outline" 
                className="bg-purple-50 text-purple-700 border-purple-200 text-[10px] sm:text-xs flex items-center gap-1 pr-1 cursor-pointer hover:bg-purple-100 transition-colors"
                data-testid="breadcrumb-hobli"
              >
                {getDisplayName(hoblis.find(h => getKey(h) === selectedHobli)) || selectedHobli} {t('newApplication.hobliSuffix')}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedHobli("");
                    setSelectedVillage("");
                    if (setViewMode) setViewMode("summary");
                  }}
                  className="ml-1 p-0.5 rounded-full hover:bg-purple-200 transition-colors"
                  title={t('common.remove') || 'Remove'}
                  data-testid="breadcrumb-hobli-close"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            </>
          )}
          {selectedVillage && (
            <>
              <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-slate-400" />
              <Badge 
                variant="outline" 
                className="bg-orange-50 text-orange-700 border-orange-200 text-[10px] sm:text-xs flex items-center gap-1 pr-1 cursor-pointer hover:bg-orange-100 transition-colors"
                data-testid="breadcrumb-village"
              >
                {getDisplayName(villages.find(v => getKey(v) === selectedVillage)) || selectedVillage}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedVillage("");
                    if (setViewMode) setViewMode("summary");
                  }}
                  className="ml-1 p-0.5 rounded-full hover:bg-orange-200 transition-colors"
                  title={t('common.remove') || 'Remove'}
                  data-testid="breadcrumb-village-close"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            </>
          )}
          
          {/* Clear Filters Button - at the end of breadcrumb */}
          {(selectedHobli || selectedVillage) && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { 
                setSelectedHobli(""); 
                setSelectedVillage(""); 
                // Reset to Summary View when clearing filters
                if (setViewMode) {
                  setViewMode("summary");
                }
              }} 
              className="ml-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 h-6 px-2 py-0"
              data-testid="clear-filters-btn"
            >
              <X className="w-3 h-3 mr-1" />
              {t('locationReports.clearFilters')}
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}
